import { FormControl, FormHelperText, FormLabel } from '@mui/material';
import { useState } from 'react';
import type { FieldComponentProps, FieldTypeSpec } from '../types';
import { FileDropZone } from './FileDropZone';
import { FileList } from './FileList';

export interface FileConfig {
  /** `accept` attribute, e.g. '.csv,.xlsx' or 'image/*'. Also enforced on drop, which the browser does not do. */
  accept?: string;
  maxSizeMb?: number;
  buttonLabel?: string;
  hint?: string;
  /** Shown instead of the generic message when a file of the wrong type is picked. */
  typeMessage?: string;
}

/** True when the file matches one of the `accept` tokens: an extension, an exact type or a `type/*` family. */
export function matchesAccept(file: File, accept: string): boolean {
  const name = file.name.toLowerCase();
  const type = file.type.toLowerCase();
  return accept.split(',').map((t) => t.trim().toLowerCase()).some((t) =>
    t.startsWith('.') ? name.endsWith(t) : t.endsWith('/*') ? type.startsWith(t.slice(0, -1)) : type === t);
}

export function fileProblem(file: File, { accept, maxSizeMb, typeMessage }: FileConfig): string | undefined {
  if (accept && !matchesAccept(file, accept)) return typeMessage ?? `${file.name} is not one of the accepted types (${accept}).`;
  if (maxSizeMb && file.size > maxSizeMb * 1024 * 1024) return `The file can be at most ${maxSizeMb} MB.`;
  return undefined;
}

/** Pick a file the page reads itself (import sheets, packages) — nothing is stored by this field. */
export function FileInput({ field, value, onChange, error }: FieldComponentProps<File | null, FileConfig>) {
  const { accept, buttonLabel, hint } = field.config;
  const [problem, setProblem] = useState<string>();
  const pick = ([file]: File[]) => {
    if (!file) return;
    const why = fileProblem(file, field.config);
    setProblem(why);
    if (!why) onChange(file);
  };
  const message = problem ?? error ?? field.helperText;
  return (
    <FormControl fullWidth={field.fullWidth} error={!!(problem ?? error)} disabled={field.disabled}
                 required={field.required} className={field.className} sx={field.sx}>
      {field.label && <FormLabel sx={{ fontSize: 12, mb: 0.5 }}>{field.label}</FormLabel>}
      <FileDropZone onFiles={pick} accept={accept} disabled={field.disabled}
                    buttonLabel={buttonLabel ?? (value ? 'Change file' : undefined)} hint={hint} {...field.props} />
      {value && (
        <FileList disabled={field.disabled}
                  items={[{ key: value.name, name: value.name, size: value.size, onRemove: () => onChange(null) }]} />
      )}
      {message && <FormHelperText>{message}</FormHelperText>}
    </FormControl>
  );
}

export const fileTypes: Record<string, FieldTypeSpec> = {
  file: {
    component: FileInput,
    check: (value, field) => fileProblem(value as File, field.config as FileConfig),
    display: (value) => (value as File).name,
  },
};
